import { useEffect, useRef, useState } from "react";
import { DropListWithIconItem } from "@cede/types";
import { TradeForm } from "@cede/types";
import { getCexLabel } from "@cede/utils";
import { enqueueSnackbar } from "notistack";

/**
 * Compare two arrays as sets (order doesn't matter)
 * Returns true if arrays contain the same elements
 */
const arraysEqualAsSet = <T>(arr1: T[] | undefined, arr2: T[] | undefined): boolean => {
  // Handle undefined/null cases
  if (arr1 === arr2) return true;
  if (!arr1 || !arr2) return false;

  // Quick length check
  if (arr1.length !== arr2.length) return false;

  // Convert to sets and compare
  const set1 = new Set(arr1);
  const set2 = new Set(arr2);

  // Check if all elements in set1 exist in set2
  return set1.size === set2.size && [...set1].every((item) => set2.has(item));
};

export type AccountSelectionConfig = {
  // Campaign configuration
  campaignId?: string;
  exchangeIds?: string[];
  preferredExchange?: string;
  keepPreviousTradeValues?: boolean;

  // Current state
  selectedAccount: TradeForm["selectedAccount"];
  availableAccounts: DropListWithIconItem[];

  // Actions
  setSelectedAccount: (account: DropListWithIconItem | null) => void;
  resetForm: (keepSelectedAccount?: boolean) => void;
  resetAmounts: () => void;
  dispatchForm: (action: { field: keyof TradeForm | "revertToken"; value?: TradeForm[keyof TradeForm] }) => void;
};

export const useAccountSelection = (config: AccountSelectionConfig) => {
  const {
    campaignId,
    exchangeIds,
    preferredExchange,
    keepPreviousTradeValues,
    selectedAccount,
    availableAccounts,
    setSelectedAccount,
    resetForm,
    resetAmounts,
    dispatchForm,
  } = config;

  // Track the last campaign to detect changes
  const lastCampaignRef = useRef<string>(undefined);
  const lastExchangeIdsRef = useRef<string[] | undefined>(undefined);

  // Track if we're in the middle of a campaign change to prevent interference
  const isHandlingCampaignChange = useRef(false);

  // Track if we're transitioning between campaigns to show loading state
  const [isTransitioning, setIsTransitioning] = useState(false);

  /**
   * Handle campaign changes with smart reset logic
   */
  useEffect(() => {
    const campaignChanged = lastCampaignRef.current !== campaignId;
    const exchangeIdsChanged = !arraysEqualAsSet(lastExchangeIdsRef.current, exchangeIds);

    if (!campaignChanged && !exchangeIdsChanged) {
      return;
    }

    // Update refs
    lastCampaignRef.current = campaignId;
    lastExchangeIdsRef.current = exchangeIds;

    // Set flags to prevent other logic from interfering and show loading state
    isHandlingCampaignChange.current = true;
    setIsTransitioning(true);

    if (!keepPreviousTradeValues) {
      resetForm(true);
      isHandlingCampaignChange.current = false;
      setIsTransitioning(false);
      return;
    }

    // Smart reset logic when keepPreviousTradeValues is true
    const currentAccount = selectedAccount;
    const shouldPreserveAccount = currentAccount && exchangeIds && exchangeIds.includes(currentAccount.cexValue);

    if (shouldPreserveAccount) {
      // Only reset amounts and tokens, preserve the account
      resetAmounts();
      dispatchForm({ field: "tokenFrom", value: null });
      dispatchForm({ field: "tokenTo", value: null });
    } else {
      // Full reset if account is not compatible
      resetForm(true);
    }

    // Clear the flags after a delay to allow normal selection logic
    setTimeout(() => {
      isHandlingCampaignChange.current = false;
      setIsTransitioning(false);
    }, 200);
  }, [campaignId, exchangeIds, keepPreviousTradeValues, selectedAccount, resetForm, resetAmounts, dispatchForm]);

  /**
   * Auto-selection logic - only runs when not handling campaign changes
   */
  useEffect(() => {
    // Skip if we're handling a campaign change
    if (isHandlingCampaignChange.current) {
      return;
    }

    const isSelectedAccountAvailable = selectedAccount
      ? availableAccounts.some((account) => account.value === selectedAccount.value)
      : false;

    // Case 1: No account selected and accounts available - auto select preferred exchange or first one
    if (availableAccounts.length > 0 && !selectedAccount) {
      const preferredAccount = preferredExchange
        ? availableAccounts.find((account) => account.cexValue === preferredExchange)
        : null;

      if (!preferredAccount && preferredExchange) {
        const exchangeLabel = getCexLabel(preferredExchange);
        enqueueSnackbar(`Connect your ${exchangeLabel} account to start trading`, { variant: "info" });
      }

      const accountToSelect = preferredAccount || availableAccounts[0];
      if (accountToSelect) {
        setSelectedAccount(accountToSelect);
        return;
      }
    }

    // Case 2: Selected account is not available anymore - switch to first available or clear
    if (selectedAccount && !isSelectedAccountAvailable) {
      if (availableAccounts.length > 0 && availableAccounts[0]) {
        setSelectedAccount(availableAccounts[0]);
      } else {
        setSelectedAccount(null);
      }
      return;
    }

    // Case 3: Exactly one account available and it's different from selected - switch to it
    if (
      availableAccounts.length === 1 &&
      selectedAccount &&
      availableAccounts[0] &&
      selectedAccount.value !== availableAccounts[0].value
    ) {
      setSelectedAccount(availableAccounts[0]);
      return;
    }

    // Case 4: No accounts available - clear selection
    if (availableAccounts.length === 0 && selectedAccount) {
      setSelectedAccount(null);
    }
  }, [availableAccounts, selectedAccount, setSelectedAccount, preferredExchange]);

  /**
   * Handle new account additions
   */
  const handleNewAccountAdded = (newAccount: DropListWithIconItem) => {
    // Skip if we're handling a campaign change
    if (isHandlingCampaignChange.current) {
      return;
    }

    setSelectedAccount(newAccount);
  };

  return {
    handleNewAccountAdded,
    isHandlingCampaignChange: isHandlingCampaignChange.current,
    isTransitioning,
  };
};
